import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDropdownClose } from '../hooks/useDropdownClose';
import { API_BASE, API_BASE_IOTHUB, authHeaders } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { Hinweis, Suche, Hinzufügen, Abbrechen, Zurück } from '../icons';
import './RegisterDeviceByType.css';

const TooltipIcon = ({ text, idSuffix }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);
  const btnRef = useRef(null);
  useDropdownClose(show, () => setShow(false), `.reg-tooltip-${idSuffix}`);

  useEffect(() => {
    if (!show) return;
    const tooltipEl = tooltipRef.current;
    const btnEl = btnRef.current;
    if (!tooltipEl || !btnEl) return;

    function positionTooltip() {
      const winW = window.innerWidth;
      const margin = 16;
      const btnRect = btnEl.getBoundingClientRect();
      const tooltipW = Math.min(300, Math.max(160, winW - 32));
      let left = Math.round(btnRect.left + btnRect.width / 2 - tooltipW / 2);
      if (left < margin) left = margin;
      if (left + tooltipW > winW - margin) left = winW - margin - tooltipW;

      tooltipEl.style.position = 'fixed';
      tooltipEl.style.top = `${Math.round(btnRect.bottom)}px`;
      tooltipEl.style.left = `${left}px`;
      tooltipEl.style.width = `${tooltipW}px`;
      tooltipEl.style.right = 'auto';
      tooltipEl.style.transform = 'none';
    }

    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    window.addEventListener('scroll', positionTooltip, true);
    return () => {
      window.removeEventListener('resize', positionTooltip);
      window.removeEventListener('scroll', positionTooltip, true);
    };
  }, [show]);

  return (
    <span className={`reg-tooltip-${idSuffix}`}>
      <button
        ref={btnRef}
        type="button"
        className={`reg-tooltip-btn${show ? ' active' : ''}`}
        aria-label="Information"
        title="Information anzeigen"
        aria-describedby={show ? `reg-tooltip-content-${idSuffix}` : undefined}
        aria-expanded={show}
        onClick={() => setShow(prev => !prev)}
        onKeyDown={e => { if (e.key === 'Escape') setShow(false); }}
      >
        <Hinweis />
      </button>
      {show && (
        <div
          id={`reg-tooltip-content-${idSuffix}`}
          role="tooltip"
          ref={tooltipRef}
          className="reg-tooltip-box"
        >
          {text}
        </div>
      )}
    </span>
  );
};

const FormSearchField = ({ id, label, value, onChange, suggestions, onSelect, onClear, number, tooltip, required, onBlur, error, errorText, disabled }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Scroll highlighted item into view when navigating with keyboard
  useEffect(() => {
    if (highlighted < 0 || !listRef.current) return;
    const item = listRef.current.children[highlighted];
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  // Focus next tabbable element after selection
  const focusNext = useCallback(() => {
    setTimeout(() => {
      const all = Array.from(document.querySelectorAll(
        'input[type="text"]:not([disabled]), input[type="number"]:not([disabled]), textarea:not([disabled]), select:not([disabled])'
      ));
      const idx = all.indexOf(inputRef.current);
      if (idx >= 0) {
        for (let i = idx + 1; i < all.length; i++) {
          all[i].focus();
          break;
        }
      }
    }, 0);
  }, []);

  const handleChange = (e) => {
    onChange(e.target.value);
    setShowSuggestions(true);
    setHighlighted(-1);
  };

  const selectItem = useCallback((item) => {
    onSelect(item);
    setShowSuggestions(false);
    setHighlighted(-1);
    focusNext();
  }, [onSelect, focusNext]);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      setHighlighted(h => Math.min(h + 1, suggestions.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlighted(h => Math.max(h - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter' && highlighted >= 0) {
      selectItem(suggestions[highlighted]);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setHighlighted(-1);
    }
  };

  return (
    <div className={`reg-field${disabled ? ' reg-field--disabled' : ''}`}>
      <div className="reg-field-label">
        <span className="reg-field-number">{number}</span>
        <span className="reg-label-text">{label}{required ? <span className="reg-required">*</span> : null}</span>
        <TooltipIcon idSuffix={id} text={tooltip} />
      </div>
      <div className="reg-input-wrapper">
        <span className="reg-input-icon"><Suche /></span>
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          className={`reg-input${error ? ' invalid' : ''}`}
          placeholder={label}
          value={value}
          autoComplete="off"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={showSuggestions}
          aria-controls={`${id}-suggestions`}
          aria-activedescendant={highlighted >= 0 && showSuggestions ? `${id}-sug-${highlighted}` : undefined}
          onChange={handleChange}
          onFocus={() => { if (!disabled) setShowSuggestions(true); }}
          onBlur={() => setTimeout(() => { setShowSuggestions(false); if (typeof onBlur === 'function') onBlur(); }, 150)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <label htmlFor={id} className="reg-floating-label">{label}</label>
        {value && (
          <button
            type="button"
            className="reg-input-clear"
            aria-label="Eingabe zurücksetzen"
            title="Eingabe zurücksetzen"
            onClick={() => { onClear(); inputRef.current?.focus(); }}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { onClear(); inputRef.current?.focus(); }}}
          >✖</button>
        )}
        {showSuggestions && suggestions.length === 0 && value && (
          <div className="reg-suggestions" role="listbox" aria-label="Keine Ergebnisse">
            <div className="reg-suggestion-no-results">Keine Ergebnisse</div>
          </div>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <div className="reg-suggestions" id={`${id}-suggestions`} role="listbox" ref={listRef}>
            {suggestions.map((s, idx) => (
              <div
                key={s.id || idx}
                id={`${id}-sug-${idx}`}
                className={`reg-suggestion${highlighted === idx ? ' highlighted' : ''}`}
                role="option"
                aria-selected={highlighted === idx}
                onMouseDown={() => selectItem(s)}
                onMouseEnter={() => setHighlighted(idx)}
              >
                {s.name}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && (
        <div className="reg-error-text">{errorText || 'Dieses Feld ist erforderlich.'}</div>
      )}
    </div>
  );
};

const RegisterDeviceByType = () => {
  const { authToken: token, iotHubToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};

  // Suchfelder (1-3)
  const [hersteller, setHersteller] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [modell, setModell] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [allProfiles, setAllProfiles] = useState([]);

  // Textfelder (4-6)
  const [devEUI, setDevEUI] = useState(prefill.devEUI || '');
  const [appKey, setAppKey] = useState(prefill.appKey || '');
  const [deviceName, setDeviceName] = useState('');

  // Gruppensuche (7)
  const [gruppe, setGruppe] = useState('');
  const [gruppeSuggestions, setGruppeSuggestions] = useState([]);
  const [selectedGruppen, setSelectedGruppen] = useState([]); // Array von { id, name }

  // touched state for validation (so errors show after user interacts)
  const [herstellerTouched, setHerstellerTouched] = useState(false);
  const [modellTouched, setModellTouched] = useState(false);
  const [devEUITouched, setDevEUITouched] = useState(false);
  const [appKeyTouched, setAppKeyTouched] = useState(false);
  const [gruppeTouched, setGruppeTouched] = useState(false);

  // Submit-State
  const [submitLoading, setSubmitLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success'); // 'success' | 'error'
  const [showErrors, setShowErrors] = useState(false);

  const showStatus = (msg, type = 'success') => {
    setStatusMessage(msg);
    setStatusType(type);
    if (type === 'success') setTimeout(() => setStatusMessage(''), 5000);
  };

  useEffect(() => {
    if (prefill.scanStatus) {
      showStatus(prefill.scanStatus, 'success');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vorschau
  const [sensorInfo] = useState(null);
  const [deviceImageUrl, setDeviceImageUrl] = useState(null);
  const [deviceImageLoading, setDeviceImageLoading] = useState(false);

  // validation rules
  const isDevEUIValid = devEUI && devEUI.length === 16;
  const isAppKeyValid = appKey && appKey.length === 32;
  const herstellerError = herstellerTouched && (!hersteller.trim() || !selectedVendor);
  const herstellerErrorText = herstellerTouched && hersteller.trim() && !selectedVendor
    ? 'Bitte einen Eintrag aus den Vorschlägen auswählen.'
    : 'Dieses Feld ist erforderlich.';
  const modellError = modellTouched && (!modell.trim() || !selectedDevice);
  const modellErrorText = modellTouched && modell.trim() && !selectedDevice
    ? 'Bitte einen Eintrag aus den Vorschlägen auswählen.'
    : 'Dieses Feld ist erforderlich.';
  const devEUIError = devEUITouched && !isDevEUIValid;
  const appKeyError = appKeyTouched && !isAppKeyValid;
  const gruppeError = gruppeTouched && !!gruppe.trim();

  // Fetch sensor image when vendor + model are selected
  useEffect(() => {
    if (!selectedDevice || !token) {
      setDeviceImageUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
      setDeviceImageLoading(false);
      return;
    }
    let cancelled = false;
    async function loadImage() {
      setDeviceImageLoading(true);
      try {
        const searchParams = new URLSearchParams({
          textSearch: selectedDevice,
          pageSize: '50',
          page: '0',
          includeSystemImages: 'true'
        });
        const res = await fetch(
          `${API_BASE}/images?${searchParams.toString()}`,
          { headers: authHeaders(token) }
        );
        if (!res.ok) throw new Error(`images search failed: ${res.status}`);
        const data = await res.json();
        const images = data.data || [];
        if (cancelled) return;
        if (images.length > 0) {
          const img = images[0];
          const imageUrl = img.link
            ? `${API_BASE.replace('/api', '')}${img.link}`
            : img.id?.id
              ? `${API_BASE}/image/${img.id.id}`
              : null;
          if (imageUrl) {
            const imgRes = await fetch(imageUrl, { headers: authHeaders(token) });
            if (!imgRes.ok) throw new Error(`image download failed: ${imgRes.status}`);
            if (cancelled) return;
            const blob = await imgRes.blob();
            setDeviceImageUrl(URL.createObjectURL(blob));
          } else {
            setDeviceImageUrl(null);
          }
        } else {
          setDeviceImageUrl(null);
        }
      } catch {
        if (!cancelled) setDeviceImageUrl(null);
      } finally {
        if (!cancelled) setDeviceImageLoading(false);
      }
    }
    loadImage();
    return () => {
      cancelled = true;
      setDeviceImageUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [selectedDevice, token]);

  useEffect(() => {
    if (!iotHubToken) return;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_IOTHUB}/api/v1/vendors/device-profiles`,
          { headers: authHeaders(iotHubToken), signal: controller.signal }
        );
        if (!res.ok) return;
        const data = await res.json();
        setAllProfiles(Array.isArray(data) ? data : []);
      } catch {}
    })();
    return () => controller.abort();
  }, [iotHubToken]);


  const herstellerSuggestions = useMemo(() => {
    const vendors = [...new Set(allProfiles.map(p => p.vendor).filter(Boolean))];
    const lower = hersteller.toLowerCase();
    return vendors
      .filter(v => !lower || v.toLowerCase().includes(lower))
      .map((v, i) => ({ id: i, name: v }));
  }, [allProfiles, hersteller]);


  const modellSuggestions = useMemo(() => {
    if (!selectedVendor) return [];
    const filtered = allProfiles.filter(p => p.vendor === selectedVendor);
    const devices = [...new Set(filtered.map(p => p.device).filter(Boolean))];
    const lower = modell.toLowerCase();
    return devices
      .filter(d => !lower || d.toLowerCase().includes(lower))
      .map((d, i) => ({ id: i, name: d }));
  }, [allProfiles, selectedVendor, modell]);


  useEffect(() => {
    if (!token) { setGruppeSuggestions([]); return; }
    const controller = new AbortController();
    (async () => {
      try {
        const params = new URLSearchParams({ pageSize: 50, page: 0, sortProperty: 'name', sortOrder: 'ASC' });
        if (gruppe) params.append('textSearch', gruppe);
        const res = await fetch(
          `${API_BASE}/entityGroups/DEVICE?${params.toString()}`,
          { headers: authHeaders(token), signal: controller.signal }
        );
        if (!res.ok) return;
        const data = await res.json();
        setGruppeSuggestions(
          (data.data || [])
            .filter(g => g.name !== 'All')
            .map(g => ({ id: g.id.id, name: g.name }))
        );
      } catch {}
    })();
    return () => controller.abort();
  }, [gruppe, token]);


  const handleSubmit = async () => {
    // Alle Felder als berührt markieren, damit Fehler angezeigt werden
    const allValid = Boolean(
      selectedVendor && selectedDevice &&
      isDevEUIValid && isAppKeyValid &&
      !gruppe.trim()
    );
    if (!allValid) {
      setHerstellerTouched(true);
      setModellTouched(true);
      setDevEUITouched(true);
      setAppKeyTouched(true);
      if (gruppe.trim()) setGruppeTouched(true);
      setShowErrors(true);
      return;
    }
    setShowErrors(false);

    setSubmitLoading(true);
    setStatusMessage('');
    try {
      const body = {
        app_key: appKey,
        cs_dev_eui: devEUI,
        model: selectedDevice,
        name: deviceName.trim() || `${selectedDevice} ${devEUI}`,
        vendor: selectedVendor,
      };
      const res = await fetch(`${API_BASE_IOTHUB}/api/v1/devices`, {
        method: 'POST',
        headers: authHeaders(iotHubToken),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        showStatus(errData.detail || errData.message || `Fehler beim Hinzufügen (HTTP ${res.status}).`, 'error');
        return;
      }
      const responseData = await res.json().catch(() => ({}));
      const tbDeviceId = responseData.tb_device_id;

      
      if (tbDeviceId && selectedGruppen.length > 0) {
        await Promise.allSettled(
          selectedGruppen.map(g =>
            fetch(`${API_BASE}/entityGroup/${g.id}/addEntities`, {
              method: 'POST',
              headers: authHeaders(token),
              body: JSON.stringify([tbDeviceId]),
            })
          )
        );
      }

      showStatus('Endgerät wurde erfolgreich hinzugefügt.', 'success');
      if (tbDeviceId) {
        setTimeout(() => navigate(`/devices/${tbDeviceId}`), 1200);
      }
    } catch {
      showStatus('Netzwerkfehler. Bitte erneut versuchen.', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="register-device-page">
      {/* Header mit Zurück-Button */}
      <div className="device-toolbar">
        <div className="device-toolbar-left">
          <button
            className="back-button"
            onClick={() => navigate('/add-device')}
            aria-label="Zurück zur Übersicht"
            title="Zurück zur Übersicht"
          >
            <span aria-hidden="true"><Zurück /></span>
          </button>
          <h1 className="device-title">Neues Endgerät hinzufügen</h1>
        </div>
      </div>

      {statusMessage && (
        <div
          className={`status-bar${statusType === 'error' ? ' status-bar--error' : ''}`}
          role="status"
          aria-live="polite"
        >
          <p>{statusMessage}</p>
        </div>
      )}
      

      <div className="reg-form-container">
        {/* Zeile 1: Hersteller, Modell */}
        <div className="reg-row reg-row-2">
          <FormSearchField
            id="reg-hersteller" label="Hersteller des Geräts" number="❶"
            tooltip="Wähle den Hersteller des Geräts aus den Vorschlägen aus."
            value={hersteller}
            onChange={v => { setHersteller(v); setSelectedVendor(''); setModell(''); setSelectedDevice(''); }}
            suggestions={herstellerSuggestions}
            onSelect={s => { setHersteller(s.name); setSelectedVendor(s.name); setModell(''); setSelectedDevice(''); }}
            onClear={() => { setHersteller(''); setSelectedVendor(''); setModell(''); setSelectedDevice(''); }}
            required
            onBlur={() => setHerstellerTouched(true)}
            error={herstellerError}
            errorText={herstellerErrorText}
          />
          <FormSearchField
            id="reg-modell" label="Modell des Geräts" number="❷"
            tooltip="Wähle das Modell des Geräts aus den Vorschlägen aus."
            value={modell}
            onChange={v => { setModell(v); setSelectedDevice(''); }}
            suggestions={modellSuggestions}
            onSelect={s => { setModell(s.name); setSelectedDevice(s.name); }}
            onClear={() => { setModell(''); setSelectedDevice(''); }}
            disabled={!selectedVendor}
            required
            onBlur={() => setModellTouched(true)}
            error={modellError}
            errorText={modellErrorText}
          />
        </div>

        {/* Übersicht über den Sensor */}
        <div className="reg-sensor-overview">
          <p className="reg-sensor-overview-title">Übersicht über den ausgewählten Sensor:</p>
          <div className="reg-sensor-overview-box">
            <div className={`reg-sensor-image${deviceImageUrl ? '' : ' reg-sensor-image--empty'}`}>
              {deviceImageLoading ? (
                <span className="reg-sensor-placeholder">Lade Bild…</span>
              ) : deviceImageUrl ? (
                <img
                  src={deviceImageUrl}
                  alt={`Bild für ${selectedVendor} ${selectedDevice}`}
                  className="reg-sensor-img"
                />
              ) : (
                <span className="reg-sensor-placeholder">Kein passendes Vorschaubild gefunden</span>
              )}
            </div>
            <div className="reg-sensor-info">
              {sensorInfo
                ? <p>{sensorInfo}</p>
                : <p>Hier werden später noch weitere Informationen zu dem ausgewählten Sensor stehen, damit Nutzende überprüfen können, ob sie tatsächlich den richtigen ausgewählt haben.</p>
              }
            </div>
          </div>
        </div>

        {/* Zeile 2: DevEUI, Application Key */}
        <div className="reg-row reg-row-2">
          <div className="reg-field">
            <div className="reg-field-label">
              <span className="reg-field-number">❸</span>
              <span className="reg-label-text">DevEUI<span className="reg-required">*</span></span>
              <TooltipIcon idSuffix="reg-deveui" text="Die DevEUI ist eine 16-stellige, einzigartige Kennung eines Sensors. In der Regel ist sie auf der Rückseite des Sensors zu finden, kann per NFC oder einen QR-Code ausgelesen werden oder ist auf dem Lieferschein vermerkt." />
            </div>
            <div className="reg-input-wrapper">
              <input
                type="text"
                id="reg-deveui"
                className={`reg-input reg-input--no-icon${devEUIError ? ' invalid' : ''}`}
                value={devEUI}
                onChange={e => setDevEUI(e.target.value)}
                onBlur={() => setDevEUITouched(true)}
                placeholder="z. B. A1B2C3D4E5F60001"
                autoComplete="off"
              />
              <label htmlFor="reg-deveui" className="reg-floating-label">
                DevEUI<span className="reg-required">*</span>
              </label>
            </div>
            {devEUIError && <div className="reg-error-text" role="alert">DevEUI muss genau 16 Zeichen lang sein.</div>}
          </div>
          <div className="reg-field">
            <div className="reg-field-label">
              <span className="reg-field-number">❹</span>
              <span className="reg-label-text">Application Key<span className="reg-required">*</span></span>
              <TooltipIcon idSuffix="reg-appkey" text="Der AppKey ist ein 32 Zeichen langer Sicherheitsschlüssel. Er wird benötigt, damit sich das Endgerät mit der Plattform verbinden kann. In der Regel ist er auf der Rückseite des Sensors zu finden, kann per NFC oder einen QR-Code ausgelesen werden oder ist auf dem Lieferschein vermerkt." />
            </div>
            <div className="reg-input-wrapper">
              <input
                type="text"
                id="reg-appkey"
                className={`reg-input reg-input--no-icon${appKeyError ? ' invalid' : ''}`}
                value={appKey}
                onChange={e => setAppKey(e.target.value)}
                onBlur={() => setAppKeyTouched(true)}
                placeholder="z. B. 00112233445566778899AABBCCDDEEFF"
                autoComplete="off"
              />
              <label htmlFor="reg-appkey" className="reg-floating-label">
                Application Key<span className="reg-required">*</span>
              </label>
            </div>
            {appKeyError && <div className="reg-error-text" role="alert">AppKey muss genau 32 Zeichen lang sein.</div>}
          </div>
        </div>

        {/* Zeile 3: Name des Geräts, Gruppe */}
        <div className="reg-row reg-row-2">
          <div className="reg-field">
            <div className="reg-field-label">
              <span className="reg-field-number">❺</span>
              <span className="reg-label-text">Name des Geräts</span>
              <TooltipIcon idSuffix="reg-name" text="Zur eindeutigen Zuordnung kannst Du dem Gerät einen individuellen Namen geben. Hier eignet sich z. B. die Seriennummer oder der Standort des Geräts. Wird das Feld nicht gesetzt, wird automatisch der Sensortyp + DevEUI als Name vergeben." />
            </div>
            <div className="reg-input-wrapper">
              <input
                type="text"
                id="reg-name"
                className="reg-input reg-input--no-icon"
                value={deviceName}
                onChange={e => setDeviceName(e.target.value)}
                placeholder="z. B. Temperatur Lager 1"
                autoComplete="off"
              />
              <label htmlFor="reg-name" className="reg-floating-label">
                Name des Geräts
              </label>
            </div>
          </div>
          <div className="reg-gruppe-wrapper">
            <FormSearchField
              id="reg-gruppe" label="Endgerätegruppen" number="❻"
              tooltip="Du kannst das Gerät direkt einer oder mehreren bestehenden Gruppen zuordnen. Gruppen helfen dir, deine Geräte zu organisieren. Du kannst auch nach der Registrierung jederzeit Gruppen hinzufügen oder ändern."
              value={gruppe}
              onChange={v => { setGruppe(v); }}
              suggestions={gruppeSuggestions.filter(s => !selectedGruppen.some(g => g.id === s.id))}
              onSelect={s => {
                setSelectedGruppen(prev =>
                  prev.some(g => g.id === s.id) ? prev : [...prev, { id: s.id, name: s.name }]
                );
                setGruppe('');
              }}
              onClear={() => { setGruppe(''); }}
              onBlur={() => setGruppeTouched(true)}
              error={gruppeError}
              errorText="Bitte einen Eintrag aus den Vorschlägen auswählen."
            />
            {selectedGruppen.length > 0 && (
              <div className="selected-badges reg-gruppe-badges">
                {selectedGruppen.map(g => (
                  <span key={g.id} className="selected-badge">
                    {g.name}
                    <button
                      type="button"
                      className="badge-remove"
                      aria-label={`Gruppe ${g.name} entfernen`}
                      title={`Gruppe ${g.name} entfernen`}
                      onClick={() => setSelectedGruppen(prev => prev.filter(x => x.id !== g.id))}
                    >✖</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pflichtfeld-Hinweis */}
        <div className="reg-required-explain" aria-hidden>
          <span className="reg-required">*</span> Pflichtfeld
        </div>

        {showErrors && (
          <p className="reg-required-fields-error" role="alert">
            Bitte fülle alle Pflichtfelder aus.
          </p>
        )}

        {/* Aktions-Buttons */}
        <div className="reg-actions">
          <button
            type="button"
            className="reg-submit-btn"
            aria-label="Endgerät hinzufügen"
            title="Endgerät hinzufügen"
            onClick={handleSubmit}
            disabled={submitLoading}
            aria-disabled={submitLoading}
          >
            {submitLoading ? 'Wird hinzugefügt…' : (
              <>
                <span className="action-button-icon"><Hinzufügen /></span>
                Endgerät hinzufügen
              </>
            )}
          </button>
          <button
            type="button"
            className="modal-secondary"
            aria-label="Abbrechen und zur Geräteauswahl zurückkehren"
            title="Abbrechen"
            onClick={() => navigate('/add-device')}
          >
            <span className="action-button-icon"><Abbrechen /></span>
            Abbrechen
          </button>
        </div>

      </div>
    </div>
  );
};

export default RegisterDeviceByType;
